import { type OverridedMixpanel, type Dict } from "mixpanel-browser";
import { type OmitFromUnion } from "types/utility-types";
import { getAdvisorEmail } from "utils/api/advisor";
import { isClient } from "utils/is-client";
import { getAdvisorId, getUtm } from "./utm";
import {
  type FeatureName,
  getFeatureKey,
  getFeatureVariation,
} from "utils/experiments/cookie-features";

export interface Properties {
  base?: Record<string, unknown>;
  experiments?: Record<string, unknown>;
}

interface ErrorEventProperties {
  error_message: string;
  error_code: string;
  component_type: string;
}

interface ComponentEventProperties {
  component_name: string;
  component_type: string;
  component_region: string;
}

interface InteractionEventProperties {
  interaction_text?: string;
  interaction_region: string;
  component_name?: string;
  interaction_type:
    | "button"
    | "checkbox"
    | "radio"
    | "floating"
    | "file"
    | "text"
    | "textarea"
    | "drop down"
    | "combo box"
    | "component"
    | "link"
    | "banner"
    | "icon";
}
export interface ExperimentEventProperties {
  experiment_name: string;
  experiment_variant: string;
  experiment_stable_id?: string;
}

export interface EventPropertyMapping {
  "page view": never;
  business: never;
  component: ComponentEventProperties;
  error: ErrorEventProperties;
  interaction: InteractionEventProperties;
  experiment: ExperimentEventProperties;
}

export type EventType = keyof EventPropertyMapping;

export type EventProperties = {
  [K in keyof EventPropertyMapping]: {
    event_type: K;
    name_of_page?: string;
  } & EventPropertyMapping[K] &
    Dict;
}[keyof EventPropertyMapping];

export type ExtractedEventType<T> = OmitFromUnion<
  Extract<
    EventProperties,
    {
      event_type: T;
    }
  >,
  "event_type"
>;

const CACHE = {
  initialized: false,
  properties: {} as Properties,
};

export function getMixpanel() {
  if (CACHE.initialized) {
    return window.mixpanel;
  }
}

export function initMixpanel(
  token: string,
  debug: boolean,
  properties: Record<string, unknown> = {},
) {
  if (!isClient() || CACHE.initialized || !window?.mixpanel) {
    return;
  }

  window.mixpanel.init(token, {
    api_host: import.meta.env.VITE_MIXPANEL_API_HOST,
    debug,
    ignore_dnt: true,
    persistence: "cookie",
    stop_utm_persistence: false,
  });

  CACHE.initialized = true;
  CACHE.properties.base = properties;
}

export function mixpanelTrack(eventName: string, properties?: Dict) {
  const mixpanel = getMixpanel();

  mixpanel?.track(eventName, {
    "Local time": new Date().toLocaleString("en-US", { hour12: false }),
    ...properties,
  });
}

export async function refreshMixpanel(
  nextUtm?: Record<string, string> | null,
  mode: "all" | "utm" = "all",
) {
  const mixpanel = getMixpanel();

  if (!mixpanel) {
    return;
  }

  if (mode === "all" && CACHE.properties.base) {
    mixpanel.register(CACHE.properties.base);
  }

  const currentUtm = getUtm();

  if (!nextUtm) {
    if (currentUtm) {
      mixpanel.register(currentUtm, { persistent: false });
    }
    return;
  } else if (currentUtm) {
    for (const key in currentUtm) {
      // Unregister persistent keys
      mixpanel.unregister(key);
    }
    mixpanel.people.unset(Object.keys(currentUtm), () => {
      mixpanel.people.set(nextUtm);
    });
  } else {
    mixpanel.people.set(nextUtm);
  }

  mixpanel.register(nextUtm, { persistent: false });

  if (nextUtm.utm_medium === "bd-traveladvisors") {
    mixpanel.set_group("advisor", nextUtm.utm_campaign);
    mixpanel.set_group("agency", nextUtm.utm_source);
  }

  const advisorId = getAdvisorId();

  if (!advisorId) {
    return;
  }

  mixpanel.people.set({
    advisor_company: nextUtm.utm_source,
    advisor_email: await getAdvisorEmail(advisorId),
    advisor_id: advisorId,
  });
}

export function updateMixpanelUser(properties?: Dict) {
  const mixpanel = getMixpanel();

  if (mixpanel && properties) {
    mixpanel.people.set(properties);
  }
}

// The methods below are exclusive to onboarding (not a part of a common module)

export async function identifyMixpanelUser(
  email: string,
  previousEmail?: string,
) {
  const mixpanel = getMixpanel();

  if (!mixpanel) {
    return;
  }

  const advisorId = getAdvisorId();
  const advisorEmail = advisorId && (await getAdvisorEmail(advisorId));

  const emailIsDifferentFromExistingAdvisorEmail =
    !!advisorEmail && previousEmail === advisorEmail && email !== advisorEmail;

  if (!!previousEmail || emailIsDifferentFromExistingAdvisorEmail) {
    // Generates a new distinct_id to avoid errAnonDistinctIdNonGUIDForm error
    mixpanel.reset();
    // re-register super properties, because a side effect of
    // "mixpanel.reset();" is clearing super properties
    void refreshMixpanel();
  }

  if (!previousEmail || emailIsDifferentFromExistingAdvisorEmail) {
    // Calling identify here creates a new profile, or associate the request to
    // an existing profile which used this email before
    mixpanel.identify(email.toLocaleLowerCase());
  } else {
    // previousEmail is already identified/aliased on Mixpanel, add the new
    // email to the identity cluster associating it to the previous value
    mixpanel.alias(
      email.toLocaleLowerCase(),
      previousEmail.toLocaleLowerCase(),
    );
  }
}

export function registerExperimentProp(experimentCookieName: FeatureName) {
  const variant = getFeatureVariation(experimentCookieName);
  const key = getFeatureKey(experimentCookieName);
  const mixpanel = getMixpanel();
  if (!key || !mixpanel || !variant) {
    return;
  }
  mixpanel.register({ [key]: variant });
}

export function registerState(state: string) {
  const mixpanel = getMixpanel();
  if (!mixpanel) {
    return;
  }
  mixpanel.register({ state_of_residence: state });
}

export function registerStatsigExperiment(
  experimentName: string,
  variant: string,
  stableId?: string,
) {
  const mixpanel = getMixpanel();

  if (!isValidExperiment(mixpanel, experimentName, variant)) {
    return;
  }

  // Mark experiment as registered
  CACHE.properties.experiments = {
    ...CACHE.properties.experiments,
    [experimentName]: true,
  };

  mixpanel.register({ [experimentName]: variant });

  const event = {
    ...(stableId && { experiment_stable_id: stableId }),
    experiment_name: experimentName,
    experiment_variant: variant,
    [experimentName]: variant,
  };
  trackExperiment("Experiment Allocated", event);
}

function isValidExperiment(
  mixpanel: OverridedMixpanel | undefined,
  experimentName: string,
  variant: string | null,
): mixpanel is OverridedMixpanel {
  return Boolean(
    mixpanel &&
      experimentName &&
      variant &&
      !CACHE.properties.experiments?.[experimentName],
  );
}

function trackEventFactory<T extends EventType>(eventType: T) {
  const UNIQUE_EVENT_KEY_CACHE = new Set<string>();
  return (
    eventName: string,
    properties: ExtractedEventType<T>,
    { onceWithUniqueKey }: { onceWithUniqueKey?: string } = {},
  ) => {
    if (onceWithUniqueKey) {
      if (UNIQUE_EVENT_KEY_CACHE.has(onceWithUniqueKey)) {
        return;
      }
      UNIQUE_EVENT_KEY_CACHE.add(onceWithUniqueKey);
    }
    mixpanelTrack(eventName, {
      event_type: eventType,
      ...properties,
    });
  };
}

export const trackInteraction = trackEventFactory("interaction");
export const trackError = trackEventFactory("error");
export const trackComponent = trackEventFactory("component");
export const trackBusiness = trackEventFactory("business");
export const trackExperiment = trackEventFactory("experiment");
